import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { PEN_STRENGTHS } from '../utils/constants';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

interface CountryPricing {
  [country: string]: {
    price: number;
    currency: string;
  };
}

interface StrengthPricing {
  [country: string]: {
    [strength: string]: {
      price: number;
      currency: string;
    };
  };
}

const AdminPricingPage: React.FC = () => {
  const { isAuthenticated, userType } = useAuth();
  const [countryPricing, setCountryPricing] = useState<CountryPricing>({});
  const [strengthPricing, setStrengthPricing] = useState<StrengthPricing>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState<'country' | 'strength'>('strength');
  
  // Country pricing edit state
  const [editCountry, setEditCountry] = useState<string | null>(null);
  const [editPrice, setEditPrice] = useState('');
  const [editCurrency, setEditCurrency] = useState('');
  
  // Strength pricing edit state
  const [editStrengthCountry, setEditStrengthCountry] = useState<string | null>(null);
  const [editStrength, setEditStrength] = useState<string>('');
  const [editStrengthPrice, setEditStrengthPrice] = useState('');
  const [editStrengthCurrency, setEditStrengthCurrency] = useState('');

  // Only allow admin users (userType check)
  const isAdmin = userType === 'admin';

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) return;
    fetchPricingData();
  }, [isAuthenticated, isAdmin, success]);

  const fetchPricingData = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      // Fetch both country and strength pricing
      const [countryRes, strengthRes] = await Promise.all([
        fetch(`${API_BASE_URL}/admin/country-pricing`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/admin/strength-pricing`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      const countryData = await countryRes.json();
      const strengthData = await strengthRes.json();
      
      setCountryPricing(countryData);
      setStrengthPricing(strengthData);
    } catch (err) {
      setError('Failed to fetch pricing data');
    } finally {
      setLoading(false);
    }
  };

  const handleCountryEdit = (country: string) => {
    setEditCountry(country);
    setEditPrice(countryPricing[country].price.toString());
    setEditCurrency(countryPricing[country].currency);
    setSuccess(false);
    setError(null);
  };

  const handleCountrySave = async () => {
    if (!editCountry || !editPrice || !editCurrency) return;
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/admin/country-pricing`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          country: editCountry,
          price: parseFloat(editPrice),
          currency: editCurrency
        })
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to update pricing');
      }
      setSuccess(true);
      setEditCountry(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStrengthEdit = (country: string, strength: string) => {
    setEditStrengthCountry(country);
    setEditStrength(strength);
    const currentPricing = strengthPricing[country]?.[strength];
    setEditStrengthPrice(currentPricing?.price?.toString() || '');
    setEditStrengthCurrency(currentPricing?.currency || 'AUD');
    setSuccess(false);
    setError(null);
  };

  const handleStrengthSave = async () => {
    if (!editStrengthCountry || !editStrength || !editStrengthPrice || !editStrengthCurrency) return;
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/admin/strength-pricing`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          country: editStrengthCountry,
          strength: parseFloat(editStrength),
          price: parseFloat(editStrengthPrice),
          currency: editStrengthCurrency
        })
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to update strength pricing');
      }
      setSuccess(true);
      setEditStrengthCountry(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return <div className="p-8 text-center">Please log in as an admin to access this page.</div>;
  }
  if (!isAdmin) {
    return <div className="p-8 text-center text-red-600">Access denied. Admins only.</div>;
  }

  return (
    <div className="max-w-6xl mx-auto p-8">
      <h1 className="text-3xl font-bold mb-6 text-purple-700">Admin: Pricing Management</h1>
      
      {/* Tab Navigation */}
      <div className="flex mb-6 border-b">
        <button
          className={`px-4 py-2 font-medium ${activeTab === 'strength' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-500 hover:text-gray-700'}`}
          onClick={() => setActiveTab('strength')}
        >
          Strength-Based Pricing
        </button>
        <button
          className={`px-4 py-2 font-medium ${activeTab === 'country' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-500 hover:text-gray-700'}`}
          onClick={() => setActiveTab('country')}
        >
          Country Default Pricing
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : error ? (
        <div className="text-red-600 mb-4 p-4 bg-red-50 rounded">{error}</div>
      ) : (
        <>
          {activeTab === 'strength' && (
            <div>
              <h2 className="text-2xl font-semibold mb-4 text-purple-600">Strength-Based Pricing by Country</h2>
              <p className="text-gray-600 mb-6">Set specific pricing for each pen strength in each country. This provides more accurate cost calculations.</p>
              
              {Object.entries(strengthPricing).map(([country, strengths]) => (
                <div key={country} className="mb-8 border rounded-lg overflow-hidden">
                  <div className="bg-purple-100 px-4 py-3 border-b">
                    <h3 className="text-lg font-semibold text-purple-800">{country}</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="p-3 text-left border-b">Strength (mg)</th>
                          <th className="p-3 text-left border-b">Price</th>
                          <th className="p-3 text-left border-b">Currency</th>
                          <th className="p-3 text-left border-b">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {PEN_STRENGTHS.map(strength => {
                          const pricing = strengths[strength.toString()];
                          return (
                            <tr key={strength} className="border-b hover:bg-gray-50">
                              <td className="p-3 font-medium">{strength} mg</td>
                              <td className="p-3">{pricing?.price || 'Not set'}</td>
                              <td className="p-3">{pricing?.currency || '-'}</td>
                              <td className="p-3">
                                <button
                                  className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600"
                                  onClick={() => handleStrengthEdit(country, strength.toString())}
                                >
                                  {pricing ? 'Edit' : 'Set'}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
              
              {editStrengthCountry && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg p-6 w-full max-w-md">
                    <h3 className="text-xl font-semibold mb-4">Edit Pricing for {editStrengthCountry} - {editStrength}mg</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Price:</label>
                        <input
                          type="number"
                          step="0.01"
                          value={editStrengthPrice}
                          onChange={e => setEditStrengthPrice(e.target.value)}
                          className="w-full border rounded px-3 py-2"
                          placeholder="Enter price"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Currency:</label>
                        <select
                          value={editStrengthCurrency}
                          onChange={e => setEditStrengthCurrency(e.target.value)}
                          className="w-full border rounded px-3 py-2"
                        >
                          <option value="AUD">AUD</option>
                          <option value="USD">USD</option>
                          <option value="GBP">GBP</option>
                          <option value="EUR">EUR</option>
                          <option value="CAD">CAD</option>
                        </select>
                      </div>
                      <div className="flex gap-2 pt-4">
                        <button
                          className="flex-1 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                          onClick={handleStrengthSave}
                          disabled={loading}
                        >
                          {loading ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          className="flex-1 bg-gray-400 text-white px-4 py-2 rounded hover:bg-gray-500"
                          onClick={() => setEditStrengthCountry(null)}
                          disabled={loading}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'country' && (
            <div>
              <h2 className="text-2xl font-semibold mb-4 text-purple-600">Country Default Pricing</h2>
              <p className="text-gray-600 mb-6">Set default pricing for countries when strength-specific pricing is not available.</p>
              
              <table className="w-full border rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-purple-100">
                    <th className="p-3 text-left border-b">Country</th>
                    <th className="p-3 text-left border-b">Default Price</th>
                    <th className="p-3 text-left border-b">Currency</th>
                    <th className="p-3 text-left border-b">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(countryPricing).map(([country, { price, currency }]) => (
                    <tr key={country} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-semibold">{country}</td>
                      <td className="p-3">{price}</td>
                      <td className="p-3">{currency}</td>
                      <td className="p-3">
                        <button
                          className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600"
                          onClick={() => handleCountryEdit(country)}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {editCountry && (
                <div className="mt-6 p-4 border rounded bg-gray-50">
                  <h3 className="text-lg font-semibold mb-3">Edit Pricing for {editCountry}</h3>
                  <div className="flex gap-4 items-end">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Price:</label>
                      <input
                        type="number"
                        step="0.01"
                        value={editPrice}
                        onChange={e => setEditPrice(e.target.value)}
                        className="border rounded px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Currency:</label>
                      <select
                        value={editCurrency}
                        onChange={e => setEditCurrency(e.target.value)}
                        className="border rounded px-3 py-2"
                      >
                        <option value="AUD">AUD</option>
                        <option value="USD">USD</option>
                        <option value="GBP">GBP</option>
                        <option value="EUR">EUR</option>
                        <option value="CAD">CAD</option>
                      </select>
                    </div>
                    <button
                      className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                      onClick={handleCountrySave}
                      disabled={loading}
                    >
                      {loading ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      className="bg-gray-400 text-white px-4 py-2 rounded hover:bg-gray-500"
                      onClick={() => setEditCountry(null)}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
      
      {success && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded shadow-lg z-50">
          Pricing updated successfully!
        </div>
      )}
    </div>
  );
};

export default AdminPricingPage; 